import React from 'react'
import { theme } from 'theme'
import { Paper, Popper } from '@mui/material'
import { makeStyles, createStyles } from '@mui/styles'

import { PartiposterProfile } from 'state/submissions'
import { SubmissionTags } from 'components/Tag'
import { GridRenderCellParams } from '@mui/x-data-grid-pro'
import ReactTooltip from 'react-tooltip'

export interface TagCellProps {
    profileData: PartiposterProfile
    submissionUuid?: string
    campaignUuid?: string
}

export const useStyles = makeStyles(() =>
    createStyles({
        root: {
            alignItems: 'center',
            lineHeight: '24px',
            width: '100%',
            height: '100%',
            position: 'relative',
            display: 'flex',
        },
    }),
)

interface GridCellExpandProps {
    tagCellProps: TagCellProps
    width?: number
}

const GridCellExpand = React.memo(function GridCellExpand(props: GridCellExpandProps) {
    const { width, tagCellProps } = props
    const wrapper = React.useRef<HTMLDivElement | null>(null)
    const cellDiv = React.useRef(null)
    const cellValue = React.useRef(null)
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
    const classes = useStyles()
    const [showFullCell, setShowFullCell] = React.useState(false)
    const [showPopper, setShowPopper] = React.useState(false)
    const { campaignUuid, profileData, submissionUuid } = tagCellProps

    const handleMouseEnter = () => {
        const isCurrentlyOverflown = profileData.tags.length > 1
        setShowPopper(isCurrentlyOverflown)
        setAnchorEl(cellDiv.current)
        setShowFullCell(true)
    }

    const handleMouseLeave = () => {
        setShowFullCell(false)
    }

    React.useEffect(() => {
        if (!showFullCell) {
            return undefined
        }

        function handleKeyDown(nativeEvent: KeyboardEvent) {
            // IE11, Edge (prior to using Bink?) use 'Esc'
            if (nativeEvent.key === 'Escape' || nativeEvent.key === 'Esc') {
                setShowFullCell(false)
            }
        }

        document.addEventListener('keydown', handleKeyDown)

        return () => {
            document.removeEventListener('keydown', handleKeyDown)
            ReactTooltip.hide()
        }
    }, [setShowFullCell, showFullCell])

    return (
        <div ref={wrapper} className={classes.root} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
            <div
                ref={cellDiv}
                style={{
                    height: 1,
                    width,
                    display: 'block',
                    position: 'absolute',
                    top: 0,
                }}
            />
            <div ref={cellValue} className="cellValue">
                <SubmissionTags
                    profileData={profileData}
                    submissionUuid={submissionUuid}
                    campaignUuid={campaignUuid}
                    coloredTags
                    maxDisplay={3}
                />
            </div>
            {showPopper && (
                <Popper
                    open={showFullCell && anchorEl !== null}
                    anchorEl={anchorEl}
                    style={{ width, marginLeft: -17, zIndex: 200 }}
                >
                    <Paper
                        elevation={1}
                        style={{
                            maxWidth: '200px',
                            display: 'flex',
                            flexWrap: 'wrap',
                            minHeight: wrapper.current!.offsetHeight - 3,
                            background: theme.newColors.neutral50,
                            padding: 8,
                            boxShadow:
                                '0px 5px 5px -3px rgba(0, 0, 0, 0.2), 0px 8px 10px 1px rgba(0, 0, 0, 0.14), 0px 3px 14px 2px rgba(0, 0, 0, 0.12)',
                        }}
                    >
                        <SubmissionTags
                            profileData={profileData}
                            submissionUuid={submissionUuid}
                            campaignUuid={campaignUuid}
                            coloredTags
                        />
                    </Paper>
                </Popper>
            )}
        </div>
    )
})

export const TagCell = (params: GridRenderCellParams) => {
    const tagCellProps: TagCellProps = params.value as TagCellProps
    return <GridCellExpand tagCellProps={tagCellProps} width={params.colDef.width} />
}
